import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import SortIcon from "../../icons/sort.svg";

export const Select = ({
  value = "newest",
  onValueChange = () => {},
  fieldLabel = "",
  options = [
    { value: "newest", label: "Newest (Default)", isDefault: true },
    { value: "oldest", label: "Oldest", isDefault: false },
  ],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  const selectedOption =
    options.find((option) => option.value === value) || options[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "ArrowDown"
      ) {
        event.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (event.key) {
      case "Escape":
        setIsOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % options.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (focusedIndex >= 0) {
          handleSelect(options[focusedIndex].value);
        }
        break;
    }
  };

  const handleSelect = (optionValue) => {
    onValueChange(optionValue);
    setIsOpen(false);
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFocusedIndex(0);
    }
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E5E5] rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 text-[#333] font-medium text-sm focus:outline-none focus:border-[#262626] focus:shadow-sm transition-colors"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Sort options"
      >
        <SortIcon className="w-4 h-4 text-[#333] mb-1" />
        <span>Sort By {fieldLabel || selectedOption.label.replace(" (Default)", "")}</span>
        <ChevronDown
          className={`w-4 h-4 text-[#333] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-[#E5E5E5] rounded-md shadow-lg z-50">
          <div className="py-1" role="listbox">
            {options.map((option, index) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors duration-150 ${
                  focusedIndex === index ||
                  (focusedIndex === -1 && option.value === value)
                    ? "bg-[#F7F6F3]"
                    : "hover:bg-[#F7F6F3]"
                } ${
                  option.value === value
                    ? "text-[#A67C52] font-semibold"
                    : "text-[#333] font-normal"
                }`}
                role="option"
                aria-selected={option.value === value}
              >
                <span>{option.label}</span>
                {option.value === value && (
                  <Check className="w-4 h-4 text-[#A67C52]" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
